/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import { graphql, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";
import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "@reach/router";
import DigiryteLogo from "../assets/Images/Homepage/Digiryte-logo.svg";
function SEO({
  description,
  lang,
  meta,
  title,
  ogimage,
  ogurl,
  ogtitle,
  keywords,
  type = "",
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
            keywords
          }
        }
      }
    `
  );
  const location = useLocation();
  const metaDescription = description || site.siteMetadata.description;
  const metaTitle = ogtitle || title || site.siteMetadata.title;

  const pathname = location.pathname;
  const canonicalPath = pathname.endsWith("/") ? pathname : `${pathname}/`;
  const canonicalUrl = `${site.siteMetadata.siteUrl}${canonicalPath}`;

  const schemaOrgJSONLD = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "DigiRyte",
      description:
        "DigiRyte is a technology advisory, audit and engineering firm helping organisations modernise, innovate and scale with confidence through Consult, Audit, Build and Scale engagements.",
      email: "hello@digiryte.com",
      image: `${DigiryteLogo}`,
      "@id": "",
      url: "https://www.digiryte.com/",
      telephone: "44 161 706 7983",
      address: {
        "@type": "PostalAddress",
        addressCountry: "UK",
        postalCode: "M50 2ST",
        streetAddress: "HOST Salford, Blue Tower, MediaCityUK, Salford",
      },
      founder: {
        "@type": "Person",
        name: "Ramesh Raja",
        gender: "Male",
        jobTitle: "CEO",
        image:
          "https://www.digiryte.com/static/Ramesh-789efa27cf2cee28291bf149a2b86d4f.png",
        sameAs: [
          "https://twitter.com/botsbreeder",
          "https://www.linkedin.com/in/dmitrygerasimenko/",
        ],
      },
      foundingDate: "2010-07-15",
      geo: {
        "@type": "GeoCoordinates",
        latitude: 53.4770585,
        longitude: -2.2439517,
      },
      sameAs: [
        "https://www.facebook.com/digiryte/",
        "https://twitter.com/digiryte",
        "https://www.linkedin.com/company/digiryte",
        "https://www.digiryte.com/",
      ],
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer service",
          email: "hello@digiryte.com",
          url: "https://www.digiryte.com",
        },
      ],
    },
  ];
  const schemaWebPageJSONLD = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: metaTitle,
      description: metaDescription,
      email: "hello@digiryte.com",
      image: `${DigiryteLogo}`,
      "@id": "",
      url: `${ogurl || canonicalUrl}`,
    },
  ];

  const schemaServicePageJSONLD = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Digital Product Services",
      provider: {
        "@type": "Organization",
        name: "DigiRyte",
        url: "https://www.digiryte.com",
      },
      areaServed: {
        "@type": "Country",
        name: "United Kingdom",
      },
      serviceType:
        "SaaS Development, App Development, UX Design, GenAI Integration, Cloud Platforms, Custom Software Solutions",
      description:
        "DigiRyte offers end-to-end digital product development services including design, development, GenAI integration, and cloud platform deployment.",
    },
  ];
  return (
    <>
      <Helmet
        htmlAttributes={{
          lang,
        }}
        title={title}
        // titleTemplate={`%s | ${site.siteMetadata.title}`}
        link={[
          {
            rel: "canonical",
            href: canonicalUrl,
          },
        ]}
        meta={[
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `og:locale`,
            content: `en_GB`,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: "og:image",
            content: ogimage,
          },
          {
            property: `og:image:width`,
            content: `1200px`,
          },
          {
            property: `og:image:height`,
            content: `630px`,
          },
          {
            property: "og:url",
            content: ogurl || canonicalUrl,
          },
          {
            property: `og:title`,
            content: metaTitle,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            name: `twitter:url`,
            content: ogurl || canonicalUrl,
          },
          {
            name: `twitter:card`,
            content: `summary_large_image`,
          },
          {
            name: "twitter:image",
            content: ogimage,
          },
          {
            name: `twitter:site`,
            content: `@digiryte`,
          },
          {
            name: `twitter:title`,
            content: metaTitle,
          },
          {
            name: `twitter:description`,
            content: metaDescription,
          },
        ].concat(meta)}
      >
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(schemaWebPageJSONLD)}
        </script>
        {type === "service" && (
          <script type="application/ld+json">
            {JSON.stringify(schemaServicePageJSONLD)}
          </script>
        )}
      </Helmet>
      {metaDescription && <p className="sr-only">{metaDescription}</p>}
    </>
  );
}
SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
};
SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
};
export default SEO;
